const APPROVED = new Set(['APROBADA', 'VALIDADA_CON_INCIDENCIA']);
const status = (value) => String(value || '').trim().toUpperCase();
const dateKey = (value) => String(value || '').slice(0, 10);
const asDate = (value) => new Date(`${value}T12:00:00Z`);

export function analysisMonths(from, to) {
  if (!from || !to || from > to) return [];
  const cursor = asDate(`${from.slice(0, 7)}-01`);
  const result = [];
  while (Number.isFinite(cursor.getTime()) && cursor.toISOString().slice(0, 10) <= to) {
    const year = cursor.getUTCFullYear();
    const month = cursor.getUTCMonth();
    const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const key = cursor.toISOString().slice(0, 7);
    result.push({
      key,
      label: cursor.toLocaleDateString('es-UY', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
      days: Array.from({ length: count }, (_, index) => {
        const date = `${key}-${String(index + 1).padStart(2, '0')}`;
        return { date, number: index + 1, monday: asDate(date).getUTCDay() === 1,
          weekday: asDate(date).toLocaleDateString('es-UY', { weekday: 'short', timeZone: 'UTC' }) };
      }),
    });
    cursor.setUTCMonth(month + 1);
  }
  return result;
}

export function classifyRestDay(rows, date, today) {
  const neutral = (reason, hours = 0) => ({ kind: 'unknown', credit: 0, hours, reason });
  if (date > today) return neutral('Día futuro');
  // Today's short shift can still grow: no half-day credit before the day closes.
  if (date === today) return neutral('Día en curso');
  if (!rows.length) return neutral('Sin información');
  const hours = rows.reduce((sum, row) => sum + Math.max(0, Number(row.horas_trabajadas) || 0), 0);
  const segments = rows.flatMap((row) => row.tramos || []);
  const hasOpenSegment = segments.some((segment) => !segment.entrada_hora || !segment.salida_hora);
  if (hasOpenSegment) return neutral('Marcas incompletas', hours);
  if (!rows.every((row) => APPROVED.has(status(row.estado_aprobacion)))) {
    return neutral('Pendiente de validación', hours);
  }
  if (hours > 0) return { kind: hours < 6 ? 'short' : 'work', credit: hours < 6 ? 0.5 : 0, hours, reason: 'Jornada validada' };
  const hasMarks = segments.length > 0 || rows.some((row) => row.entrada_hora || row.salida_hora || row.marcas_ids?.length);
  if (!hasMarks && rows.every((row) => status(row.estado_turno) === 'LIBRE')) {
    return { kind: 'free', credit: 1, hours: 0, reason: 'Libre confirmado' };
  }
  return neutral(hasMarks ? 'Marcas sin duración computable' : 'Sin descanso confirmado', hours);
}

export function buildRestMatrix(journals, months, today) {
  const grouped = new Map();
  journals.forEach((row) => {
    const personId = Number(row.persona_id);
    if (!grouped.has(personId)) grouped.set(personId, new Map());
    const days = grouped.get(personId);
    const key = dateKey(row.fecha);
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(row);
  });
  const result = new Map();
  grouped.forEach((byDay, personId) => {
    result.set(personId, Object.fromEntries(months.map((month) => {
      const days = Object.fromEntries(month.days.map(({ date }) => [date, classifyRestDay(byDay.get(date) || [], date, today)]));
      const elapsed = month.days.filter(({ date }) => date < today);
      const taken = elapsed.reduce((sum, { date }) => sum + days[date].credit, 0);
      const expected = elapsed.length * 1.5 / 7;
      return [month.key, { days, expected, taken, pending: Math.max(0, expected - taken),
        unknown: elapsed.filter(({ date }) => days[date].kind === 'unknown').length }];
    })));
  });
  return result;
}

export function emptyRestMonth(month, today) {
  const elapsed = month.days.filter(({ date }) => date < today).length;
  const expected = elapsed * 1.5 / 7;
  return { days: Object.fromEntries(month.days.map(({ date }) => [date, classifyRestDay([], date, today)])),
    expected, taken: 0, pending: expected, unknown: elapsed };
}

export function restDaysText(value) {
  return Number(value).toLocaleString('es-UY', { maximumFractionDigits: 2 });
}
